import React, { useState } from 'react';
import AppLayout from '@/components/Layouts/AppLayout';
import { Progress } from '@/components/ui/progress';
import { OtrosTramitesHeader } from './OtrosTramitesHeader';
import { OtrosTramitesFilters } from './OtrosTramitesFilters';
import { OtrosTramitesTable } from './OtrosTramitesTable';
import { ManageOtroTramiteDialog } from './dialogs/ManageOtroTramiteDialog';
import { ViewOtroTramiteDialog } from './dialogs/ViewOtroTramiteDialog';
import { PersonasListDialog } from './dialogs/PersonasListDialog';
import { ConfirmDeleteDialog } from './dialogs/ConfirmDeleteDialog';
import { GestionCobrosDialog } from './dialogs/GestionCobrosDialog';
import { RetirarCopiasDialog } from './retiros/RetirarCopiasDialog';
import { RetiroMasivoDialog } from './retiros/RetiroMasivoDialog';
import { ViewRetiroDialog } from './retiros/ViewRetiroDialog';
import { ArchivosDialog } from './ArchivosDialog';
import WhatsAppOtroTramiteRetiroModal from '@/components/WhatsAppOtroTramiteRetiroModal';
import QuickAssignUserDialog from '@/components/QuickAssignUserDialog';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { useAlert } from '@/hooks/useAlert';
import { useOtrosTramites } from '../hooks/useOtrosTramites';
import { OtrosTramitesColumnVisibilityProvider } from '../contexts/OtrosTramitesColumnVisibilityContext';
import type { ManageState, OtroTramiteRecord } from '../types';
import { Button } from '@/components/ui/button';

const OtrosTramitesPage: React.FC = () => {
  const { canManageTramites, canSendWhatsApp } = useAuthorization();
  const { successAlert, errorAlert } = useAlert();
  const {
    tramites,
    search,
    setSearch,
    retiro,
    setRetiro,
    retiroOptions,
    cobro,
    setCobro,
    cobroOptions,
    factura,
    setFactura,
    facturaOptions,
    tipo,
    setTipo,
    tipoOptions,
    assignedUserId,
    assignedUser,
    setAssignedUser,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    listQuery,
    isLoading,
    isFetching,
    create,
    isCreating,
    update,
    isUpdating,
    updateMontos,
    remove,
    isRemoving,
  } = useOtrosTramites();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [retiroMasivoOpen, setRetiroMasivoOpen] = useState(false);

  const eligibleIds = React.useMemo(
    () => tramites.filter((t) => t.copiasTotales - t.copiasActuales > 0).map((t) => t.id),
    [tramites]
  );

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAll = () => {
    const allSelected = eligibleIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleIds));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const [manageState, setManageState] = useState<ManageState | null>(null);
  const [viewingTramite, setViewingTramite] = useState<OtroTramiteRecord | null>(null);
  const [deletingTramite, setDeletingTramite] = useState<OtroTramiteRecord | null>(null);
  const [retirandoTramite, setRetirandoTramite] = useState<OtroTramiteRecord | null>(null);
  const [viewingRetiroHistorial, setViewingRetiroHistorial] = useState<OtroTramiteRecord | null>(null);
  const [gestionandoCobros, setGestionandoCobros] = useState<OtroTramiteRecord | null>(null);
  const [gestionandoArchivos, setGestionandoArchivos] = useState<{
    tramite: OtroTramiteRecord;
    scope: 'factura' | 'otros';
  } | null>(null);
  const [viewingPersonas, setViewingPersonas] = useState<{
    tramite: OtroTramiteRecord;
    type: 'otorgantes' | 'responsables' | 'garantes';
  } | null>(null);
  const [quickAssignTramite, setQuickAssignTramite] = useState<OtroTramiteRecord | null>(null);
  const [whatsAppRetiroTramite, setWhatsAppRetiroTramite] = useState<OtroTramiteRecord | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleCreateTramite = () => {
    setManageState({ mode: 'create' });
  };

  const handleViewTramite = (tramite: OtroTramiteRecord) => {
    setViewingTramite(tramite);
  };

  const handleEditTramite = (tramite: OtroTramiteRecord) => {
    setManageState({ mode: 'edit', tramite });
  };

  const handleDeleteTramite = (tramite: OtroTramiteRecord) => {
    setDeleteError(null);
    setDeletingTramite(tramite);
  };

  const handleRetirarCopias = (tramite: OtroTramiteRecord) => {
    setRetirandoTramite(tramite);
  };

  const handleManageCobros = (tramite: OtroTramiteRecord) => {
    setGestionandoCobros(tramite);
  };

  const handleViewRetiroHistorial = (tramite: OtroTramiteRecord) => {
    setViewingRetiroHistorial(tramite);
  };

  const handleManageArchivos = (tramite: OtroTramiteRecord) => {
    setGestionandoArchivos({ tramite, scope: 'otros' });
  };

  const handleManageFacturas = (tramite: OtroTramiteRecord) => {
    setGestionandoArchivos({ tramite, scope: 'factura' });
  };

  const handleViewOtorgantes = (tramite: OtroTramiteRecord) => {
    setViewingPersonas({ tramite, type: 'otorgantes' });
  };

  const handleViewResponsables = (tramite: OtroTramiteRecord) => {
    setViewingPersonas({ tramite, type: 'responsables' });
  };

  const handleViewGarantes = (tramite: OtroTramiteRecord) => {
    setViewingPersonas({ tramite, type: 'garantes' });
  };

  const handleSendWhatsAppRetiro = (tramite: OtroTramiteRecord) => {
    setWhatsAppRetiroTramite(tramite);
  };

  const handleEditAssignedUser = (tramite: OtroTramiteRecord) => {
    setQuickAssignTramite(tramite);
  };

  const handleUpdateMontos = async (montoARS: number, montoUSD: number) => {
    if (!gestionandoCobros) return;
    const updated = await updateMontos({ id: gestionandoCobros.id, montoARS, montoUSD });

    // Actualizar el estado local con los montos actualizados
    setGestionandoCobros(updated);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTramite) return;
    try {
      setDeleteError(null);
      await remove(deletingTramite.id);
      setDeletingTramite(null);
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'No pudimos eliminar el trámite. Intentá nuevamente.'
      );
    }
  };

  const handleQuickAssignSave = async (assignedUser) => {
    if (!quickAssignTramite) return;

    try {
      await update({
        id: quickAssignTramite.id,
        payload: {
          assignedUserIds: assignedUser ? [assignedUser.id] : [],
        },
      });
      successAlert({
        message: assignedUser
          ? 'Responsable actualizado con éxito'
          : 'Responsable removido con éxito',
      });
      setQuickAssignTramite(null);
    } catch (error) {
      errorAlert({
        title: 'Error al actualizar responsable',
        exception: error,
      });
      throw error;
    }
  };

  const handleSortChanged = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  return (
    <AppLayout title="Otros Trámites">
      <OtrosTramitesColumnVisibilityProvider>
      <div className="space-y-2">
        <Progress
          value={100}
          className={`h-1 ${isFetching || isLoading ? 'opacity-100 animate-pulse' : 'opacity-0'} transition-opacity duration-200`}
        />

        <OtrosTramitesHeader onCreate={handleCreateTramite} />

        <OtrosTramitesFilters
          search={search}
          onSearchChange={setSearch}
          retiro={retiro}
          onRetiroChange={setRetiro}
          retiroOptions={retiroOptions}
          cobro={cobro}
          onCobroChange={setCobro}
          cobroOptions={cobroOptions}
          factura={factura}
          onFacturaChange={setFactura}
          facturaOptions={facturaOptions}
          tipo={tipo}
          onTipoChange={setTipo}
          tipoOptions={tipoOptions}
          assignedUser={assignedUserId ? assignedUser : null}
          onAssignedUserChange={setAssignedUser}
        />

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} trámite{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}
            </span>
            <Button
              size="sm"
              onClick={() => setRetiroMasivoOpen(true)}
            >
              Retiro masivo
            </Button>
            <button
              type="button"
              className="ml-auto text-sm text-blue-600 hover:underline"
              onClick={handleClearSelection}
            >
              Limpiar selección
            </button>
          </div>
        )}

        <OtrosTramitesTable
          tramites={tramites}
          isLoading={isLoading || isFetching}
          isError={listQuery.isError}
          sortConfig={{ field: sortField, direction: sortDirection }}
          onSortChanged={handleSortChanged}
          onView={handleViewTramite}
          onEdit={handleEditTramite}
          onDelete={handleDeleteTramite}
          onRetirar={handleRetirarCopias}
          onViewRetiroHistorial={handleViewRetiroHistorial}
          onManageCobros={handleManageCobros}
          onManageFacturas={handleManageFacturas}
          onManageArchivos={handleManageArchivos}
          onSendWhatsAppRetiro={canSendWhatsApp ? handleSendWhatsAppRetiro : undefined}
          onViewOtorgantes={handleViewOtorgantes}
          onViewResponsables={handleViewResponsables}
          onViewGarantes={handleViewGarantes}
          onEditAssignedUser={canManageTramites ? handleEditAssignedUser : undefined}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleAll={handleToggleAll}
        />

        {quickAssignTramite && (
          <QuickAssignUserDialog
            open={Boolean(quickAssignTramite)}
            onOpenChange={(open) => {
              if (!open) {
                setQuickAssignTramite(null);
              }
            }}
            entityLabel={`el otro trámite #${quickAssignTramite.id}`}
            initialAssignedUser={quickAssignTramite.assignedUsers?.[0] ?? null}
            isSubmitting={isUpdating}
            onSubmit={handleQuickAssignSave}
          />
        )}

        {manageState && (
          <ManageOtroTramiteDialog
            open={Boolean(manageState)}
            mode={manageState.mode}
            tramite={manageState.tramite}
            onOpenChange={(open) => {
              if (!open) {
                setManageState(null);
              }
            }}
            onSubmit={(payload) =>
              manageState.mode === 'create'
                ? create(payload)
                : update({ id: manageState.tramite!.id, payload })
            }
            isSubmitting={manageState.mode === 'create' ? isCreating : isUpdating}
          />
        )}

        {viewingTramite && (
          <ViewOtroTramiteDialog
            open={Boolean(viewingTramite)}
            tramite={viewingTramite}
            onOpenChange={(open) => {
              if (!open) {
                setViewingTramite(null);
              }
            }}
          />
        )}

        {deletingTramite && (
          <ConfirmDeleteDialog
            open={Boolean(deletingTramite)}
            tramite={deletingTramite}
            onOpenChange={(open) => {
              if (!open) {
                setDeletingTramite(null);
                setDeleteError(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            isDeleting={isRemoving}
            error={deleteError}
          />
        )}

        {retirandoTramite && (
          <RetirarCopiasDialog
            open={Boolean(retirandoTramite)}
            tramite={retirandoTramite}
            onOpenChange={(open) => {
              if (!open) {
                setRetirandoTramite(null);
              }
            }}
          />
        )}

        {viewingRetiroHistorial && (
          <ViewRetiroDialog
            open={Boolean(viewingRetiroHistorial)}
            tramite={viewingRetiroHistorial}
            onOpenChange={(open) => {
              if (!open) {
                setViewingRetiroHistorial(null);
              }
            }}
          />
        )}

        {gestionandoCobros && (
          <GestionCobrosDialog
            open={Boolean(gestionandoCobros)}
            tramite={gestionandoCobros}
            onOpenChange={(open) => {
              if (!open) {
                setGestionandoCobros(null);
              }
            }}
            onUpdateMontos={handleUpdateMontos}
          />
        )}

        <ArchivosDialog
          tramite={gestionandoArchivos?.tramite ?? null}
          open={Boolean(gestionandoArchivos)}
          scope={gestionandoArchivos?.scope ?? 'otros'}
          onOpenChange={(open) => {
            if (!open) {
              setGestionandoArchivos(null);
            }
          }}
        />

        {viewingPersonas && (
          <PersonasListDialog
            open={Boolean(viewingPersonas)}
            tramite={viewingPersonas.tramite}
            type={viewingPersonas.type}
            onOpenChange={(open) => {
              if (!open) {
                setViewingPersonas(null);
              }
            }}
          />
        )}

        {canSendWhatsApp && whatsAppRetiroTramite && (
          <WhatsAppOtroTramiteRetiroModal
            open={Boolean(whatsAppRetiroTramite)}
            onOpenChange={(open) => {
              if (!open) {
                setWhatsAppRetiroTramite(null);
              }
            }}
            tramite={whatsAppRetiroTramite}
          />
        )}

        <RetiroMasivoDialog
          open={retiroMasivoOpen}
          onOpenChange={(open) => {
            setRetiroMasivoOpen(open);
          }}
          selectedTramites={tramites.filter((t) => selectedIds.has(t.id))}
          onSuccess={handleClearSelection}
        />
      </div>
      </OtrosTramitesColumnVisibilityProvider>
    </AppLayout>
  );
};

export default OtrosTramitesPage;
